import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  BrowserRouter,
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
} from 'react-router-dom';

import '@/index.scss';
import { setDefaultTimezone } from '@/shared/dayjs/export'; // first: installs the dayjs plugins
import '@/core/http/export';
import '@/app/bootstrap/ga-setup';
import '@/app/bootstrap/daily-reload';
import { Context } from '@/core/app-context/export';
import {
  TextScaleProvider,
  localPreferencesService,
  LocalPreferences,
} from '@/core/preferences/export';
import {
  AuthProvider,
  LoginPage,
  TokenExchangePage,
  Auth0Page,
  Auth0TokenBridge,
  authService,
  useSessionId,
  BbgPage,
} from '@/core/auth/export';
import DockPage from '@/app/dock/dock-page';
import { VerifyContactPage, NotificationProvider } from '@/core/notifications/export';
import { CalendarProvider, CalendarContext } from '@/modules/calendar/export';
import DashboardPage from '@/app/dashboard/pages/dashboard-page';
import { Auth0Provider } from '@auth0/auth0-react';
import {
  convertWidgetToReactRoute,
  dashboardWidgetRegistry,
  convertWidgetFactoryToReactRoute,
  ActiveMobileWidgetProvider,
} from '@/core/widgets/export';
import ConnectionIndicators from '@/app/connection-indicators';
import {
  setAppRouter,
  SidePanelShell,
  DeepDiveWindowPage,
  isSidePanelInline,
} from '@/core/deep-dive/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsTypes,
  TrackingInit,
} from '@/shared/analytics/export';
import { installService } from '@/shared/install/export';
import { themeService } from '@/shared/theme/export';
import { PdfViewer, ImageViewer } from '@/shared/pdf-viewer/export';

import 'react-toastify/dist/ReactToastify.css';
import { ArticleViewPage, articleApi } from '@/modules/articles/export';
import { ToastsWrapper } from '@/core/toasts/export';
import TabsPage from '@/app/tabs/tabs-page';
import { RequirePermissions, Permissions } from '@/core/permissions/export';
import { ChatDrawer } from '@/core/chat/export';
import { SettingsDrawerShell } from '@/core/settings/export';
import { WidgetNavigationProvider } from '@/core/widget-navigation/export';
import { useTimer } from '@/shared/hooks/export';
import '@/app/deep-dive-widgets'; // registers the deep-dive widgets
import '@/app/dashboard-widgets'; // registers the dashboard's widgets
import '@/app/tabs-widgets'; // registers the tabs surface's widgets
import '@/app/search-result-types'; // registers the feature-specific global-search result types
import '@/app/chat-embeds'; // registers the components chat messages can embed
import '@/app/settings-sections'; // registers feature sections in the settings drawer

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
const auth0Client = process.env.REACT_APP_AUTH0_CLIENT_ID || '';

installService.registerInstallAnalytics(() => authService.getSubjectData());

const perHostDefaultPage =
  process.env[
    `REACT_APP_DEFAULT_PATH_${window.location.hostname.toUpperCase().replaceAll('.', '_')}`
  ];

function App() {
  const [loaded, setLoaded] = useState<boolean>();
  const [preferences, setPreferences] = useState<LocalPreferences>();

  //initialize sessionId
  const sessionId = useSessionId();
  const subject = authService.getSubjectData();

  // Built once — a new router every render makes RouterProvider re-init from
  // the URL, which was racing the side panel's own hash parse on load.
  const [router] = useState(() => {
    let widgetRoutes: RouteObject[] = [];

    dashboardWidgetRegistry.getAll().forEach(value => {
      widgetRoutes = [...widgetRoutes, ...convertWidgetToReactRoute(value)];
    });

    dashboardWidgetRegistry.getFactories().forEach(value => {
      widgetRoutes = [...widgetRoutes, ...convertWidgetFactoryToReactRoute(value)];
    });

    const router = createBrowserRouter([
      {
        path: '/',
        element: perHostDefaultPage ? (
          <Navigate to={perHostDefaultPage} />
        ) : (
          <CalendarProvider>
            <CalendarContext.Consumer>
              {value => <Navigate to={`/calendar/${value?.view}/${value?.date}`} />}
            </CalendarContext.Consumer>
          </CalendarProvider>
        ),
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/article',
        element: <AuthProvider />,
        shouldRevalidate: a => {
          console.log(a);
          return false;
        },
        children: [
          {
            path: '/article/:articleId',
            element: <ArticleViewPage />,
            loader: async ({ params }) => {
              try {
                return await articleApi.getArticle(params.articleId || '');
              } catch (e) {
                return e;
              }
            },
          },
        ],
      },

      {
        path: '/auth0',
        element: <Auth0Page />,
      },
      {
        path: '/bbg-sso',
        element: <BbgPage />,
      },
      {
        path: '/token',
        element: <TokenExchangePage />,
      },
      {
        path: '/token/:token',
        element: <TokenExchangePage />,
      },
      {
        path: '/notifications/verify-contact',
        element: <VerifyContactPage />,
      },
      {
        path: '/dock',
        element: <AuthProvider />,
        shouldRevalidate: a => {
          console.log(a);
          return false;
        },
        children: [
          {
            path: '/dock',
            element: <DockPage />,
          },
        ],
      },
      {
        path: '/dashboard',
        element: (
          <ConnectionIndicators>
            <AuthProvider />
          </ConnectionIndicators>
        ),
        shouldRevalidate: a => {
          console.log(a);
          return false;
        },
        children: [
          {
            path: '/dashboard',
            element: <DashboardPage />,
          },
        ],
      },
      {
        path: '/tabs',
        element: (
          <ConnectionIndicators>
            <AuthProvider />
          </ConnectionIndicators>
        ),
        shouldRevalidate: a => {
          console.log(a);
          return false;
        },
        children: [
          {
            path: '/tabs',
            element: <TabsPage />,
          },
        ],
      },
      {
        path: '/deep-dive-window',
        element: <AuthProvider />,
        shouldRevalidate: () => false,
        children: [
          {
            path: '/deep-dive-window',
            element: <DeepDiveWindowPage navigationProvider={WidgetNavigationProvider} />,
          },
        ],
      },
      ...widgetRoutes.map(
        value =>
          ({
            path: value.path,
            element: (
              <ConnectionIndicators>
                <AuthProvider />

                <RequirePermissions allow={Permissions.ALPHA_ACCESS_CHAT}>
                  <ChatDrawer />
                </RequirePermissions>
              </ConnectionIndicators>
            ),
            children: [value],
          }) as RouteObject,
      ),
    ]);

    setAppRouter(router);

    return router;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(authService.tokenValid());
  useTimer(2000, () => setIsAuthenticated(authService.tokenValid()));
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => router.subscribe(state => setPathname(state.location.pathname)), [router]);

  useEffect(() => {
    localPreferencesService.getLocalPreferences().then((value: any) => {
      if (value.timezoneType === 'explicit') setDefaultTimezone(value.timezone);
      else setDefaultTimezone();

      if (value) themeService.setThemeBasedOnUserPreferences(value);

      setLoaded(true);
      setPreferences(value);
    });
  }, []);

  useEffect(() => {
    const applyCopyPermission = () => {
      const permissions = authService.getCredentials()?.body?.permissions ?? [];
      document
        .getElementById('html-root-element')
        ?.setAttribute('data-allow-copy', String(permissions.includes(Permissions.COPY_NEWS_FEED)));
    };

    authService.onTokenAvailable().then(applyCopyPermission);

    return authService.onPermissionsChanged(applyCopyPermission);
  }, []);

  useEffect(() => {
    let pingInterval: NodeJS.Timer | null = null;
    if (subject) {
      pingInterval = setInterval(() => {
        analyticsService.recordAnalyticsEvent(AnalyticsEvent.PING, {
          subject,
          object: { type: AnalyticsTypes.PING },
          session: {
            id: sessionId || null,
          },
        });
      }, Number(process.env.REACT_APP_PING_EVENT_INTERVAL));
    }
    return () => {
      pingInterval && clearInterval(pingInterval);
    };
  }, [sessionId, subject]);

  useEffect(() => {
    const timeout = setInterval(() => {
      localPreferencesService.getLocalPreferences().then(value => {
        if (!preferences) return;

        if (dayjs(preferences.lastUpdated).isBefore(dayjs(value.lastUpdated))) {
          console.warn('Updating local preferences');
          if (value) themeService.setThemeBasedOnUserPreferences(value);
          setPreferences(value);
        }
      });
    }, 5000);

    return () => {
      clearInterval(timeout);
    };
  }, [preferences]);

  const suppressInlineSidePanel = !isSidePanelInline(pathname);

  if (!loaded || !preferences) return <></>;

  return (
    <div className="App">
      <TextScaleProvider>
        <NotificationProvider>
          <Auth0Provider
            domain={auth0Domain}
            clientId={auth0Client}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            cacheLocation="localstorage"
            authorizationParams={{
              redirect_uri: `${window.location.origin}/auth0`,
              scope: 'openid profile email offline_access',
            }}
          >
            <Auth0TokenBridge />
            <Context.Provider value={{ showMenu: true, localPreferences: preferences }}>
              {/* Fallback useOpenWidget() for anything not already nested inside a
                  host's own WidgetNavigationProvider (dashboard.tsx, tab-widget-host.tsx,
                  side-panel-shell.tsx, dock-page.tsx) — those still take precedence for
                  their descendants since context resolves to the nearest provider. This
                  outer one exists so open-Settings-by-id works from truly anywhere. */}
              <WidgetNavigationProvider>
                <ActiveMobileWidgetProvider>
                  <RouterProvider router={router} />
                  <BrowserRouter>
                    <TrackingInit />
                  </BrowserRouter>
                  <ImageViewer />
                  <PdfViewer
                    getAccessToken={() => authService.getAccessToken().then(t => t?.access_token)}
                  />
                  {isAuthenticated && !suppressInlineSidePanel && (
                    <SidePanelShell navigationProvider={WidgetNavigationProvider} />
                  )}
                  {isAuthenticated && <SettingsDrawerShell />}
                </ActiveMobileWidgetProvider>
              </WidgetNavigationProvider>
            </Context.Provider>
            <ToastsWrapper />
          </Auth0Provider>
        </NotificationProvider>
      </TextScaleProvider>
    </div>
  );
}

export default App;
